import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Link,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  WhatsApp,
  CheckCircle,
  Error,
  Info,
  Visibility,
  VisibilityOff,
  ExpandMore,
  Launch,
  ContentCopy
} from '@mui/icons-material';
import api from '../services/api';

interface WhatsAppChannelData {
  name: string;
  provider: string;
  phone_number: string;
  provider_config: Record<string, any>;
  webhook_verify_token?: string;
  business_name?: string;
  business_description?: string;
  business_website?: string;
  business_email?: string;
  greeting_enabled: boolean;
  greeting_message: string;
  auto_assignment_enabled: boolean;
}

interface ProviderInfo {
  name: string;
  description: string;
  features: string[];
  setup_complexity: string;
  documentation_url: string;
}

interface ProviderField {
  type: string;
  label: string;
  required: boolean;
  default?: any;
  options?: string[];
  description: string;
}

interface WhatsAppChannelConfigProps {
  onChannelCreated: (channel: any) => void;
  editingChannel?: any;
  onCancel: () => void;
}

const WhatsAppChannelConfig: React.FC<WhatsAppChannelConfigProps> = ({
  onChannelCreated,
  editingChannel,
  onCancel
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  
  const [formData, setFormData] = useState<WhatsAppChannelData>({
    name: '',
    provider: '',
    phone_number: '',
    provider_config: {},
    webhook_verify_token: '',
    business_name: '',
    business_description: '',
    business_website: '',
    business_email: '',
    greeting_enabled: true,
    greeting_message: 'Hello! Welcome to our WhatsApp support. How can we help you today?',
    auto_assignment_enabled: true
  });
  
  const [availableProviders, setAvailableProviders] = useState<Record<string, ProviderInfo>>({});
  const [providerFields, setProviderFields] = useState<Record<string, ProviderField>>({});
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [webhookUrl, setWebhookUrl] = useState('');

  useEffect(() => {
    loadAvailableProviders();
    if (editingChannel) {
      // Load existing channel data
      loadChannelData();
    }
  }, [editingChannel]);

  useEffect(() => {
    if (selectedProvider) {
      loadProviderFields(selectedProvider);
    }
  }, [selectedProvider]);

  const loadAvailableProviders = async () => {
    try {
      const response = await api.get('/whatsapp/providers');
      setAvailableProviders(response.data);
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
  };

  const loadProviderFields = async (provider: string) => {
    try {
      const response = await api.get(`/whatsapp/providers/${provider}/config`);
      setProviderFields(response.data.fields);
      
      // Initialize provider_config with default values
      const defaultConfig: Record<string, any> = {};
      Object.entries(response.data.fields).forEach(([key, field]) => {
        if ((field as ProviderField).default !== undefined) {
          defaultConfig[key] = (field as ProviderField).default;
        }
      });
      
      setFormData(prev => ({
        ...prev,
        provider: provider,
        provider_config: { ...defaultConfig, ...prev.provider_config }
      }));
    } catch (error) {
      console.error('Failed to load provider fields:', error);
    }
  };

  const loadChannelData = async () => {
    if (!editingChannel) return;

    console.log('🔍 [WhatsApp] Loading channel data for editingChannel:', editingChannel);
    console.log('🔍 [WhatsApp] Channel ID:', editingChannel.id);
    console.log('🔍 [WhatsApp] WhatsApp Channel ID:', editingChannel.whatsapp_channel_id);

    try {
      const response = await api.get(`/whatsapp/channels/${editingChannel.id}`);
      console.log('✅ [WhatsApp] Loaded channel data:', response.data);
      const channelData = response.data;
      
      setFormData({
        name: editingChannel.name,
        provider: channelData.provider || 'meta',
        phone_number: channelData.phone_number,
        provider_config: channelData.provider_config || {},
        webhook_verify_token: '', // Don't pre-fill for security
        business_name: channelData.business_profile?.business_name || '',
        business_description: channelData.business_profile?.business_description || '',
        business_website: channelData.business_profile?.website || '',
        business_email: channelData.business_profile?.email || '',
        greeting_enabled: editingChannel.greeting_enabled,
        greeting_message: editingChannel.greeting_message || 'Hello! Welcome to our WhatsApp support. How can we help you today?',
        auto_assignment_enabled: editingChannel.auto_assignment_enabled
      });
      
      setSelectedProvider(channelData.provider || 'meta');
      setVerified(channelData.verified);
      setWebhookUrl(channelData.webhook_url || '');
    } catch (error) {
      console.error('Failed to load channel data:', error);
    }
  };

  const steps = [
    {
      label: 'Provider Selection',
      description: 'Choose your WhatsApp Business API provider'
    },
    {
      label: 'Provider Configuration',
      description: 'Configure provider-specific settings'
    },
    {
      label: 'Channel Settings',
      description: 'Configure channel behavior and messages'
    },
    {
      label: 'Webhook Setup',
      description: 'Configure webhook for receiving messages'
    }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleProviderConfigChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      provider_config: { ...prev.provider_config, [field]: value }
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleProviderSelect = (provider: string) => {
    setSelectedProvider(provider);
    setFormData(prev => ({ ...prev, provider, provider_config: {} }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    switch (step) {
      case 0: // Provider Selection
        if (!selectedProvider) newErrors.provider = 'Please select a provider';
        break;
      case 1: // Provider Configuration
        if (!formData.phone_number) newErrors.phone_number = 'Phone number is required';
        
        // Validate provider-specific required fields
        Object.entries(providerFields).forEach(([fieldKey, field]) => {
          if (field.required && !formData.provider_config[fieldKey]) {
            newErrors[fieldKey] = `${field.label} is required`;
          }
        });
        break;
      case 2: // Channel Settings
        if (!formData.name) newErrors.name = 'Channel name is required';
        if (formData.greeting_enabled && !formData.greeting_message) {
          newErrors.greeting_message = 'Greeting message is required when greeting is enabled';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleVerifyCredentials = async () => {
    if (!validateStep(1)) return;
    
    setVerifying(true);
    try {
      // For now, just mark as verified since we'll verify during channel creation
      setVerified(true);
      
      // You could add a separate verification endpoint here if needed
      // const response = await api.post('/whatsapp/verify', {
      //   provider: formData.provider,
      //   provider_config: formData.provider_config
      // });
      
    } catch (error: any) {
      console.error('Verification failed:', error);
      setErrors({ general: error.response?.data?.detail || 'Verification failed' });
    } finally {
      setVerifying(false);
    }
  };

  const handleCreateChannel = async () => {
    if (!validateStep(2)) return;
    
    setLoading(true);
    try {
      if (editingChannel) {
        const response = await api.put(`/whatsapp/channels/${editingChannel.id}`, formData);
        onChannelCreated(response.data);
      } else {
        const response = await api.post('/whatsapp/channels', formData);
        onChannelCreated(response.data);
      }
    } catch (error: any) {
      console.error('Failed to create/update channel:', error);
      setErrors({ general: error.response?.data?.detail || 'Failed to save channel' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Choose WhatsApp Provider
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select your WhatsApp Business API provider. Each provider offers different features and setup requirements.
            </Typography>
            
            <Grid container spacing={2}>
              {Object.entries(availableProviders).map(([key, provider]) => (
                <Grid item xs={12} md={6} key={key}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedProvider === key ? 2 : 1,
                      borderColor: selectedProvider === key ? 'primary.main' : 'divider',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => handleProviderSelect(key)}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {provider.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {provider.description}
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Setup Complexity: {provider.setup_complexity}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Features: {provider.features.join(', ')}
                        </Typography>
                      </Box>
                      
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Launch />}
                        href={provider.documentation_url}
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Documentation
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {selectedProvider && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {availableProviders[selectedProvider]?.name} selected. Click Continue to proceed with configuration.
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {availableProviders[selectedProvider]?.name} Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure your {availableProviders[selectedProvider]?.name} WhatsApp Business API settings.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  value={formData.phone_number}
                  onChange={(e) => handleInputChange('phone_number', e.target.value)}
                  fullWidth
                  required
                  error={!!errors.phone_number}
                  helperText={errors.phone_number || 'Include country code (e.g., +1234567890)'}
                  placeholder="+1234567890"
                />
              </Grid>
              
              {/* Render provider-specific fields */}
              {Object.entries(providerFields).map(([fieldKey, field]) => (
                <Grid item xs={12} sm={6} key={fieldKey}>
                  {field.type === 'text' || field.type === 'password' ? (
                    <TextField
                      label={field.label}
                      type={field.type === 'password' && !showToken ? 'password' : 'text'}
                      value={formData.provider_config[fieldKey] || ''}
                      onChange={(e) => handleProviderConfigChange(fieldKey, e.target.value)}
                      fullWidth
                      required={field.required}
                      error={!!errors[fieldKey]}
                      helperText={errors[fieldKey] || field.description}
                      InputProps={field.type === 'password' ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowToken(!showToken)}
                              edge="end"
                            >
                              {showToken ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      } : undefined}
                    />
                  ) : field.type === 'select' ? (
                    <FormControl fullWidth>
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        value={formData.provider_config[fieldKey] || field.default || ''}
                        onChange={(e) => handleProviderConfigChange(fieldKey, e.target.value)}
                        label={field.label}
                      >
                        {field.options?.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : field.type === 'boolean' ? (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.provider_config[fieldKey] || field.default || false}
                          onChange={(e) => handleProviderConfigChange(fieldKey, e.target.checked)}
                        />
                      }
                      label={field.label}
                    />
                  ) : null}
                </Grid>
              ))}
              
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleVerifyCredentials}
                  disabled={verifying}
                  startIcon={verifying ? <CircularProgress size={20} /> : <CheckCircle />}
                  fullWidth
                >
                  {verifying ? 'Verifying...' : 'Verify Credentials'}
                </Button>
                
                {verified && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Credentials verified successfully!
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Channel Settings
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Channel Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  fullWidth
                  required
                  error={!!errors.name}
                  helperText={errors.name || 'Display name for this WhatsApp channel'}
                  placeholder="WhatsApp Support"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Business Name"
                  value={formData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  fullWidth
                  helperText="Your business name as shown to customers"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Business Description"
                  value={formData.business_description}
                  onChange={(e) => handleInputChange('business_description', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Brief description of your business"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.greeting_enabled}
                      onChange={(e) => handleInputChange('greeting_enabled', e.target.checked)}
                    />
                  }
                  label="Enable Greeting Message"
                />
              </Grid>
              
              {formData.greeting_enabled && (
                <Grid item xs={12}>
                  <TextField
                    label="Greeting Message"
                    value={formData.greeting_message}
                    onChange={(e) => handleInputChange('greeting_message', e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    required={formData.greeting_enabled}
                    error={!!errors.greeting_message}
                    helperText={errors.greeting_message || 'First message sent to new contacts'}
                  />
                </Grid>
              )}
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.auto_assignment_enabled}
                      onChange={(e) => handleInputChange('auto_assignment_enabled', e.target.checked)}
                    />
                  }
                  label="Enable Auto Assignment"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Webhook Configuration
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Configure this webhook URL in your Meta Developer Console to receive messages.
            </Alert>
            
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Webhook URL
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={webhookUrl || `${window.location.origin}/api/v1/whatsapp/webhook/{channel_id}`}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => copyToClipboard(webhookUrl)}
                            size="small"
                          >
                            <ContentCopy />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">Webhook Setup Instructions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  1. Go to your Meta Developer Console
                </Typography>
                <Typography variant="body2" paragraph>
                  2. Navigate to WhatsApp → Configuration
                </Typography>
                <Typography variant="body2" paragraph>
                  3. Add the webhook URL above
                </Typography>
                <Typography variant="body2" paragraph>
                  4. Use this verify token: <Chip label={formData.webhook_verify_token || 'Auto-generated'} size="small" />
                </Typography>
                <Typography variant="body2" paragraph>
                  5. Subscribe to these webhook fields:
                </Typography>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body2">• messages</Typography>
                  <Typography variant="body2">• message_deliveries</Typography>
                  <Typography variant="body2">• message_reads</Typography>
                  <Typography variant="body2">• messaging_handovers</Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <WhatsApp color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5">
          {editingChannel ? 'Edit' : 'Create'} WhatsApp Channel
        </Typography>
      </Box>

      {errors.general && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.general}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>
              {step.label}
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                {step.description}
              </Typography>
              
              {renderStepContent(index)}

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={index === steps.length - 1 ? handleCreateChannel : handleNext}
                  disabled={loading || (index === 0 && !selectedProvider)}
                  sx={{ mr: 1 }}
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : index === steps.length - 1 ? (
                    editingChannel ? 'Update Channel' : 'Create Channel'
                  ) : (
                    'Continue'
                  )}
                </Button>
                {index > 0 && (
                  <Button onClick={handleBack} sx={{ mr: 1 }}>
                    Back
                  </Button>
                )}
                <Button onClick={onCancel}>
                  Cancel
                </Button>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Paper>
  );
};

export default WhatsAppChannelConfig;